/**
 * App.js — Culp Financial Planner
 *
 * Root component: auth gate, bottom navigation (mobile-first), page routing.
 * Navigation: state-based routing via `page` state (no React Router).
 *
 * Phase 7 additions:
 *   - SW update banner (orange bar, "Update Now" button)
 *   - Install prompt capture (beforeinstallprompt → SettingsPage)
 *   - Foreground push toast (dark toast, auto-dismiss 5s)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CategoryProvider } from './contexts/CategoryContext';
import { applyUpdate } from './services/swBridge';
import { onForegroundMessage } from './services/notifications';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import BillsPage from './pages/BillsPage';
import TransactionsPage from './pages/TransactionsPage';
import BudgetPage from './pages/BudgetPage';
import ReportsPage from './pages/ReportsPage';
import GoalsPage from './pages/GoalsPage';
import NetWorthPage from './pages/NetWorthPage';
import SettingsPage from './pages/SettingsPage';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

function AppContent() {
  const { user } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [pushToast, setPushToast] = useState(null);

  // Listen for SW update available
  useEffect(() => {
    const handler = () => setUpdateAvailable(true);
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);

  // Capture beforeinstallprompt for install button
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Foreground push toast listener
  useEffect(() => {
    const unsub = onForegroundMessage(({ title, body }) => {
      setPushToast({ title, body });
      setTimeout(() => setPushToast(null), 5000);
    });
    return unsub;
  }, []);

  /** Handle install prompt accepted */
  const handleInstalled = useCallback(() => {
    setInstallPrompt(null);
  }, []);

  if (!user) return <LoginPage />;

  const renderPage = () => {
    switch (page) {
      case 'dashboard': return <DashboardPage setPage={setPage} />;
      case 'bills': return <BillsPage />;
      case 'transactions': return <TransactionsPage />;
      case 'budget': return <BudgetPage />;
      case 'reports': return <ReportsPage />;
      case 'goals': return <GoalsPage />;
      case 'networth': return <NetWorthPage />;
      case 'settings':
        return (
          <SettingsPage
            installPrompt={installPrompt}
            onInstalled={handleInstalled}
          />
        );
      default: return <DashboardPage />;
    }
  };

  const navItems = [
    { id: 'dashboard', label: 'Home', icon: '🏠' },
    { id: 'bills', label: 'Bills', icon: '📋' },
    { id: 'transactions', label: 'Activity', icon: '💳' },
    { id: 'budget', label: 'Budget', icon: '📊' },
    { id: 'reports', label: 'Reports', icon: '📄' },
    { id: 'goals', label: 'Goals', icon: '🎯' },
    { id: 'networth', label: 'Net Worth', icon: '💰' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
  ];

  return (
    <div className="app">
      {/* SW Update Banner */}
      {updateAvailable && (
        <div className="update-banner">
          <span>A new version is available</span>
          <button className="update-banner-btn" onClick={applyUpdate}>
            Update Now
          </button>
        </div>
      )}

      {/* Top header bar */}
      <header className="top-bar">
        <h1 className="top-bar-title">OKSpend</h1>
        <span className="top-bar-user">{(user.user_metadata?.full_name || user.email)?.split(' ')[0]}</span>
      </header>

      {/* Page content */}
      <main className="main-content">
        <ErrorBoundary key={page}>
          {renderPage()}
        </ErrorBoundary>
      </main>

      {/* Push Toast */}
      {pushToast && (
        <div className="push-toast" onClick={() => setPushToast(null)}>
          <div className="push-toast-title">{pushToast.title}</div>
          <div className="push-toast-body">{pushToast.body}</div>
        </div>
      )}

      {/* Bottom navigation (mobile-first) */}
      <nav className="bottom-nav">
        {navItems.map(item => (
          <button
            key={item.id}
            className={`bottom-nav-item ${page === item.id ? 'active' : ''}`}
            onClick={() => setPage(item.id)}
          >
            <span className="bottom-nav-icon">{item.icon}</span>
            <span className="bottom-nav-label">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <CategoryProvider>
        <AppContent />
      </CategoryProvider>
    </AuthProvider>
  );
}

export default App;
